import React from 'react';

interface AppearanceStepProps {
    data: {
        appearance: {
            theme: string;
            primaryColor: string;
            position: string;
        };
    };
    updateData: (data: Partial<any>) => void;
}

const AppearanceStep: React.FC<AppearanceStepProps> = ({ data, updateData }) => {
    const handleAppearanceChange = (key: string, value: string) => {
        updateData({
            appearance: {
                ...data.appearance,
                [key]: value
            }
        });
    };

    // Predefined color options
    const colorOptions = [
        { name: 'Blau', value: '#3b82f6' },
        { name: 'Grün', value: '#10b981' },
        { name: 'Rot', value: '#ef4444' },
        { name: 'Lila', value: '#8b5cf6' },
        { name: 'Orange', value: '#f59e0b' },
        { name: 'Pink', value: '#ec4899' },
    ];

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-medium text-gray-900">Erscheinungsbild</h2>
            <p className="text-gray-500">
                Passe das Aussehen deines Chatbots an, um ihn optimal in deine Website zu integrieren.
            </p>

            <div>
                <label className="block text-sm font-medium text-gray-700">Design-Theme</label>
                <div className="mt-2 grid grid-cols-3 gap-3">
                    <label className={`${
                        data.appearance.theme === 'light' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'
                    } border rounded-md p-3 flex items-center justify-center cursor-pointer transition-colors`}>
                        <input
                            type="radio"
                            name="theme"
                            value="light"
                            checked={data.appearance.theme === 'light'}
                            onChange={() => handleAppearanceChange('theme', 'light')}
                            className="sr-only"
                        />
                        <span>Hell</span>
                    </label>
                    <label className={`${
                        data.appearance.theme === 'dark' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'
                    } border rounded-md p-3 flex items-center justify-center cursor-pointer transition-colors`}>
                        <input
                            type="radio"
                            name="theme"
                            value="dark"
                            checked={data.appearance.theme === 'dark'}
                            onChange={() => handleAppearanceChange('theme', 'dark')}
                            className="sr-only"
                        />
                        <span>Dunkel</span>
                    </label>
                    <label className={`${
                        data.appearance.theme === 'system' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'
                    } border rounded-md p-3 flex items-center justify-center cursor-pointer transition-colors`}>
                        <input
                            type="radio"
                            name="theme"
                            value="system"
                            checked={data.appearance.theme === 'system'}
                            onChange={() => handleAppearanceChange('theme', 'system')}
                            className="sr-only"
                        />
                        <span>System</span>
                    </label>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                    Das Theme bestimmt die Gesamtfarbgebung deines Chatbots.
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Primärfarbe</label>
                <div className="mt-2">
                    <div className="grid grid-cols-6 gap-3 mb-3">
                        {colorOptions.map((color) => (
                            <div
                                key={color.value}
                                className={`${
                                    data.appearance.primaryColor === color.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                                } relative rounded-md cursor-pointer`}
                                onClick={() => handleAppearanceChange('primaryColor', color.value)}
                            >
                                <div
                                    className="h-8 rounded-md border border-gray-200"
                                    style={{ backgroundColor: color.value }}
                                    title={color.name}
                                ></div>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center">
                        <label className="block text-sm text-gray-700 mr-2">Benutzerdefiniert:</label>
                        <div className="flex items-center">
                            <input
                                type="color"
                                value={data.appearance.primaryColor}
                                onChange={(e) => handleAppearanceChange('primaryColor', e.target.value)}
                                className="h-8 w-8 border border-gray-300 rounded-md cursor-pointer"
                            />
                            <input
                                type="text"
                                value={data.appearance.primaryColor}
                                onChange={(e) => handleAppearanceChange('primaryColor', e.target.value)}
                                className="ml-2 border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm"
                            />
                        </div>
                    </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                    Die Primärfarbe wird für Buttons, Hervorhebungen und den Chat-Button verwendet.
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Position auf der Webseite</label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                    <label className={`${
                        data.appearance.position === 'right' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'
                    } border rounded-md p-3 flex items-center justify-center cursor-pointer transition-colors`}>
                        <input
                            type="radio"
                            name="position"
                            value="right"
                            checked={data.appearance.position === 'right'}
                            onChange={() => handleAppearanceChange('position', 'right')}
                            className="sr-only"
                        />
                        <span>Rechts unten</span>
                    </label>
                    <label className={`${
                        data.appearance.position === 'left' ? 'bg-blue-50 border-blue-500' : 'border-gray-200'
                    } border rounded-md p-3 flex items-center justify-center cursor-pointer transition-colors`}>
                        <input
                            type="radio"
                            name="position"
                            value="left"
                            checked={data.appearance.position === 'left'}
                            onChange={() => handleAppearanceChange('position', 'left')}
                            className="sr-only"
                        />
                        <span>Links unten</span>
                    </label>
                </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Vorschau</h3>
                <div className="relative h-64 border border-gray-300 rounded-md overflow-hidden">
                    <div className={`absolute ${data.appearance.position === 'right' ? 'right-0' : 'left-0'} bottom-0 m-4`}>
                        <div className={`flex flex-col items-${data.appearance.position === 'right' ? 'end' : 'start'}`}>
                            <button
                                className={`w-12 h-12 rounded-full mb-2 flex items-center justify-center text-white shadow-lg`}
                                style={{ backgroundColor: data.appearance.primaryColor }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-blue-700">
                            Du kannst das Erscheinungsbild deines Chatbots jederzeit in den Einstellungen ändern.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppearanceStep;